import { useEffect, useRef, useState } from "preact/hooks";
import type { SubmitFeedbackInput } from "../types";
import { collectBrowserInfo } from "../capture/browser";
import { snapshotConsole } from "../capture/console";
import { snapshotErrors } from "../capture/errors";
import { snapshotNetwork } from "../capture/network";
import { captureScreenshot } from "../capture/screenshot";
import {
  ANNOTATION_COLOR,
  ANNOTATION_STROKE_WIDTH_PCT,
  burnAnnotationsToBlob,
  freeDrawSvgPath,
  newId,
  STROKE_PRESETS,
  strokeWidthPctOf,
  type Annotation,
  type Arrow,
  type Pin,
} from "../capture/annotations";
import { getOrCreateClientId } from "../api/client-id";
import { submitFeedback, uploadMedia, type WidgetApiConfig } from "../api/client";
import { activeTeamCode, readProfile } from "../api/profile";
import { TypePicker, type FeedbackType } from "./TypePicker";
import {
  ArrowToolIcon,
  CloseIcon,
  CommentPinIcon,
  DragIcon,
  PencilIcon,
  RectangleIcon,
  SendIcon,
  UndoIcon,
} from "./icons";

type Tool = "pin" | "rectangle" | "arrow" | "freedraw";
type Phase = "capturing" | "annotating" | "submitting" | "success" | "error";

type Props = {
  apiConfig: WidgetApiConfig;
  rootElement: HTMLElement;
  onClose: () => void;
  onSubmitted?: () => void;
};

// Min drag distance (px) before a rect/arrow stroke counts as deliberate.
// Below this we treat the click as accidental and drop the shape.
const MIN_DRAG_PX = 4;

/**
 * Runs an async submit step and, on failure, rethrows with a human label so
 * the surfaced error names the step that broke (e.g. "Couldn't upload
 * screenshot: Load failed"). Critical for debugging mobile-only failures where
 * we can't see the device's console.
 */
async function step<T>(label: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(`Couldn't ${label}: ${detail}`);
  }
}

export function ScreenshotMode({ apiConfig, rootElement, onClose, onSubmitted }: Props) {
  const [phase, setPhase] = useState<Phase>("capturing");
  const [shotBlob, setShotBlob] = useState<Blob | null>(null);
  const [shotUrl, setShotUrl] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool>("pin");
  // Active stroke thickness (% of min side) applied to newly drawn shapes.
  const [strokePct, setStrokePct] = useState<number>(ANNOTATION_STROKE_WIDTH_PCT);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [drafting, setDrafting] = useState<Annotation | null>(null);
  const [activePinId, setActivePinId] = useState<string | null>(null);
  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  // canvasRef — the inner wrapper div; pins are absolutely positioned inside it.
  // imgRef   — the actual <img> element; always use THIS for coordinate math.
  // The wrapper and img have the same dimensions (wrapper sizes to content),
  // but measuring the img directly is safest — avoids any rounding from
  // a wrapper that could theoretically be 1px off.
  const canvasRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  // The screenshot's native pixel dimensions. The annotation SVG uses these as
  // its viewBox so shapes are rendered in native-pixel coordinates that scale
  // UNIFORMLY down to the displayed size — the same math as the burned result.
  // This is what makes the live preview match the saved image (WYSIWYG). We
  // deliberately avoid vectorEffect="non-scaling-stroke" here: Preact emits it
  // camelCased and the browser ignores it, so strokeWidth would be read as
  // viewBox units and render ~10x too thick.
  const [imgNat, setImgNat] = useState<{ w: number; h: number } | null>(null);

  function measureImage() {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    setImgNat({ w: img.naturalWidth, h: img.naturalHeight });
  }

  useEffect(() => {
    measureImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shotUrl]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        await new Promise((r) => requestAnimationFrame(r));
        const blob = await captureScreenshot(rootElement);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        createdUrl = url;
        setShotBlob(blob);
        setShotUrl(url);
        setPhase("annotating");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Screenshot failed");
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stageCoords(e: PointerEvent | MouseEvent): { xPct: number; yPct: number } {
    // ALWAYS measure the <img> element directly. The img's getBoundingClientRect
    // gives the exact rendered pixel dimensions with no letterboxing ambiguity —
    // regardless of what the wrapper div reports. This is the single source of
    // truth for all coordinate math.
    const img = imgRef.current;
    if (!img) return { xPct: 50, yPct: 50 };
    const rect = img.getBoundingClientRect();
    return {
      xPct: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      yPct: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  }

  function onStagePointerDown(e: PointerEvent) {
    if (e.target !== e.currentTarget) return;
    if (phase === "submitting") return;
    const { xPct, yPct } = stageCoords(e);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    if (tool === "pin") {
      // Forgiving placement. If a pin's note is already open, treat this click
      // as "dismiss that note" instead of dropping yet another pin — this kills
      // the "every click spawns another dot" frustration. You drop one pin,
      // write the note, tap away to close it, and only a *fresh* click (with
      // nothing selected) adds the next one.
      if (activePinId !== null) {
        setActivePinId(null);
        dragStartRef.current = null;
        return;
      }
      const pin: Pin = { type: "pin", id: newId(), xPct, yPct, note: "" };
      setAnnotations((a) => [...a, pin]);
      setActivePinId(pin.id);
      dragStartRef.current = null; // Pin is a click — no drag tracking.
    } else if (tool === "rectangle") {
      setDrafting({ type: "rectangle", id: newId(), x1Pct: xPct, y1Pct: yPct, x2Pct: xPct, y2Pct: yPct, strokeWidthPct: strokePct });
    } else if (tool === "arrow") {
      setDrafting({ type: "arrow", id: newId(), x1Pct: xPct, y1Pct: yPct, x2Pct: xPct, y2Pct: yPct, strokeWidthPct: strokePct });
    } else if (tool === "freedraw") {
      setDrafting({ type: "freedraw", id: newId(), pointsPct: [{ xPct, yPct }], strokeWidthPct: strokePct });
    }
  }

  function onStagePointerMove(e: PointerEvent) {
    if (!drafting) return;
    const { xPct, yPct } = stageCoords(e);
    if (drafting.type === "rectangle" || drafting.type === "arrow") {
      setDrafting({ ...drafting, x2Pct: xPct, y2Pct: yPct });
    } else if (drafting.type === "freedraw") {
      setDrafting({
        ...drafting,
        pointsPct: [...drafting.pointsPct, { xPct, yPct }],
      });
    }
  }

  function onStagePointerUp(e: PointerEvent) {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    if (!drafting) return;
    // For rect/arrow, ignore tiny drags (accidental clicks).
    if (drafting.type === "rectangle" || drafting.type === "arrow") {
      const start = dragStartRef.current;
      const dist = start
        ? Math.hypot(e.clientX - start.x, e.clientY - start.y)
        : Infinity;
      if (dist < MIN_DRAG_PX) {
        setDrafting(null);
        dragStartRef.current = null;
        return;
      }
    }
    setAnnotations((a) => [...a, drafting]);
    setDrafting(null);
    dragStartRef.current = null;
  }

  function updatePin(id: string, patch: Partial<Pin>) {
    setAnnotations((a) =>
      a.map((ann) => (ann.type === "pin" && ann.id === id ? { ...ann, ...patch } : ann)),
    );
  }

  function deleteAnnotation(id: string) {
    setAnnotations((a) => a.filter((ann) => ann.id !== id));
    if (activePinId === id) setActivePinId(null);
  }

  function undoLast() {
    setAnnotations((a) => a.slice(0, -1));
    setActivePinId(null);
  }

  async function submit() {
    if (!shotBlob) return;
    if (!title.trim()) {
      setError("Please add a title for this feedback.");
      return;
    }

    setPhase("submitting");
    setError(null);

    try {
      // Burn rectangles/arrows/freedraws onto a fresh blob at native screenshot
      // resolution. Pins stay vector — dashboard renders them as interactive
      // numbered markers instead of pixels.
      // Each step is labelled so a failure (e.g. iOS Safari "Load failed" on a
      // cross-origin fetch) tells us exactly where it broke instead of surfacing
      // a bare, ambiguous message.
      const finalBlob = await step("prepare image", () =>
        burnAnnotationsToBlob(shotBlob, annotations),
      );

      const mediaId = await step("upload screenshot", () =>
        uploadMedia(apiConfig, finalBlob, "layar.png"),
      );

      // Snapshot the page geometry now so the dashboard or widget can
      // re-anchor pins later on the same URL even after the user has
      // scrolled or resized. xPct/yPct are still % of the screenshot
      // (viewport at capture time); pageX/pageY are document-absolute.
      const innerW = window.innerWidth;
      const innerH = window.innerHeight;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      const pageWidth = Math.max(
        document.documentElement.scrollWidth,
        document.body?.scrollWidth ?? 0,
      );
      const pageHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body?.scrollHeight ?? 0,
      );

      const pins = annotations
        .filter((a): a is Pin => a.type === "pin")
        .map((p) => ({
          xPct: p.xPct,
          yPct: p.yPct,
          note: p.note.trim() || undefined,
          // Document-absolute coordinates of where the user clicked.
          pageX: scrollX + (p.xPct / 100) * innerW,
          pageY: scrollY + (p.yPct / 100) * innerH,
          pageWidth,
          pageHeight,
          scrollY,
        }));

      // Pull the tester's name/role from their saved Profile. Name rides along
      // as the reporter; teamCode (internal only) lets the server tag source.
      const profile = readProfile();
      const reporterName = profile.name.trim() || undefined;
      const reporterEmail = email.trim() || undefined;

      const payload: Omit<SubmitFeedbackInput, "key"> = {
        type,
        severity: "medium",
        title: title.trim(),
        description: description.trim() || undefined,
        url: location.href,
        referrer: document.referrer || undefined,
        reporter: reporterName || reporterEmail ? { name: reporterName, email: reporterEmail } : undefined,
        code: activeTeamCode(profile),
        browserId: getOrCreateClientId(),
        mediaIds: [mediaId],
        diagnostics: {
          browser: collectBrowserInfo(),
          consoleLogs: snapshotConsole(),
          network: snapshotNetwork(),
          errors: snapshotErrors(),
          pins,
        },
      };

      await step("send feedback", () => submitFeedback(apiConfig, payload));
      setPhase("success");
      onSubmitted?.();
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submit failed");
      setPhase("annotating");
    }
  }

  if (phase === "capturing") {
    return (
      <div class="mf-overlay mf-overlay-capturing" role="status">
        <div class="mf-spinner" aria-hidden="true" />
        <div>Capturing screenshot…</div>
      </div>
    );
  }

  if (phase === "error" && !shotUrl) {
    return (
      <div class="mf-overlay mf-overlay-error" role="alert">
        <div class="mf-overlay-msg">{error ?? "Couldn't capture screenshot."}</div>
        <button type="button" class="mf-btn mf-btn-ghost" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  const pinAnnotations = annotations.filter((a): a is Pin => a.type === "pin");
  const activePin = pinAnnotations.find((p) => p.id === activePinId) ?? null;
  const shapeAnnotations = drafting
    ? [...annotations.filter((a) => a.type !== "pin"), drafting]
    : annotations.filter((a) => a.type !== "pin");
  const cursor =
    phase === "submitting"
      ? "wait"
      : tool === "freedraw"
        ? "crosshair"
        : tool === "pin"
          ? "crosshair"
          : "crosshair";

  return (
    <div class="mf-overlay">
      <div class="mf-overlay-toolbar" role="toolbar" aria-label="Annotation tools">
        <span class="mf-tool mf-tool-handle" aria-hidden="true">
          <DragIcon />
        </span>
        <ToolButton current={tool} value="pin" onSelect={setTool} label="Comment pin">
          <CommentPinIcon />
        </ToolButton>
        <ToolButton current={tool} value="rectangle" onSelect={setTool} label="Rectangle">
          <RectangleIcon />
        </ToolButton>
        <ToolButton current={tool} value="arrow" onSelect={setTool} label="Arrow">
          <ArrowToolIcon />
        </ToolButton>
        <ToolButton current={tool} value="freedraw" onSelect={setTool} label="Free draw">
          <PencilIcon />
        </ToolButton>
        <span class="mf-tool-sep" aria-hidden="true" />
        {STROKE_PRESETS.map((preset) => (
          <ThicknessButton
            key={preset.key}
            active={strokePct === preset.pct}
            dotPx={preset.key === "s" ? 4 : preset.key === "m" ? 7 : 11}
            label={`${preset.label} line`}
            onSelect={() => setStrokePct(preset.pct)}
          />
        ))}
        <span class="mf-tool-sep" aria-hidden="true" />
        <button
          type="button"
          class="mf-tool"
          onClick={undoLast}
          disabled={annotations.length === 0}
          title="Undo last annotation"
          aria-label="Undo"
        >
          <UndoIcon />
        </button>
        <button
          type="button"
          class="mf-tool"
          onClick={onClose}
          title="Cancel and close"
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Mobile-only scrim behind the pin note bottom sheet. Rendered at the
          overlay level (not inside the stage) so its z-index sits above the
          screenshot but below the note form. Tap to dismiss. CSS hides it on
          desktop, where the note is a popover beside the pin. */}
      {phase !== "success" && activePinId !== null && (
        <div
          class="mf-pin-scrim"
          onPointerDown={() => setActivePinId(null)}
          aria-hidden="true"
        />
      )}

      {phase === "success" ? (
        <div class="mf-success mf-success-overlay">
          <div class="mf-success-icon">✓</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Thanks for the feedback!</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
            We&apos;ll take a look.
          </div>
        </div>
      ) : (
        /* mf-stage is a flex centering container. mf-img-wrap (canvasRef) is
           sized EXACTLY to the rendered image — no letterbox bars. Clicks and
           pin coordinates are relative to mf-img-wrap, so they map 1:1 to the
           screenshot. Previously the stage used background-size:contain which
           letterboxed the image inside the stage div, making stageCoords()
           return values offset by the bar sizes. */
        <div class="mf-stage" ref={stageRef}>
          <div
            class="mf-img-wrap"
            ref={canvasRef}
            onPointerDown={onStagePointerDown}
            onPointerMove={onStagePointerMove}
            onPointerUp={onStagePointerUp}
            onPointerCancel={onStagePointerUp}
            style={{ cursor, touchAction: "none" }}
          >
            {shotUrl && (
              <img
                ref={imgRef}
                src={shotUrl}
                alt="Screenshot"
                class="mf-stage-img"
                draggable={false}
                onLoad={measureImage}
              />
            )}
            <svg
              class="mf-stage-svg"
              preserveAspectRatio="none"
              viewBox={`0 0 ${imgNat?.w ?? 100} ${imgNat?.h ?? 100}`}
            >
              {shapeAnnotations.map((a) => (
                <ShapeSvg key={a.id} annotation={a} imgNat={imgNat} />
              ))}
            </svg>
            {pinAnnotations.map((pin, idx) => (
              <PinMarker
                key={pin.id}
                pin={pin}
                index={idx + 1}
                active={activePinId === pin.id}
                onActivate={() => setActivePinId(pin.id)}
                disabled={phase === "submitting"}
              />
            ))}
            {/* Note editor lives HERE (not inside the marker) so it isn't
                trapped by the marker's stacking context / transform. The anchor
                only positions it for the desktop popover; on mobile the form
                becomes a fixed bottom sheet (see CSS). */}
            {activePin && (
              <div
                class="mf-pin-note-anchor"
                style={{ left: `${activePin.xPct}%`, top: `${activePin.yPct}%` }}
              >
                <div
                  key={activePin.id}
                  class={`mf-pin-form mf-pin-form-${activePin.xPct > 60 ? "left" : "right"}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <textarea
                    class="mf-textarea mf-textarea-compact"
                    placeholder="Note for this spot (optional)"
                    value={activePin.note}
                    onInput={(e) =>
                      updatePin(activePin.id, { note: (e.target as HTMLTextAreaElement).value })
                    }
                    maxLength={500}
                    disabled={phase === "submitting"}
                    autoFocus
                    rows={2}
                  />
                  <div class="mf-pin-form-actions">
                    <button
                      type="button"
                      class="mf-btn-text mf-btn-text-danger"
                      onClick={() => deleteAnnotation(activePin.id)}
                      disabled={phase === "submitting"}
                    >
                      Delete pin
                    </button>
                    <button
                      type="button"
                      class="mf-btn-text mf-btn-text-done"
                      onClick={() => setActivePinId(null)}
                      disabled={phase === "submitting"}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}
            {annotations.length === 0 && (
              <div class="mf-stage-hint">{toolHint(tool)}</div>
            )}
          </div>
        </div>
      )}

      {phase !== "success" && (
        <div class="mf-overlay-footer">
          {error && <div class="mf-error mf-error-overlay">{error}</div>}
          <div class="mf-footer-form">
            {/* Jenis berdiri di ATAS judul: ia satu ketukan, dan menaruhnya di bawah
                membuat orang menemukannya sesudah selesai mengetik — saat ia sudah
                bersiap menekan kirim. */}
            <TypePicker value={type} onChange={setType} disabled={phase === "submitting"} />
            <input
              class="mf-input"
              placeholder="What's the issue? (required)"
              value={title}
              onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
              maxLength={200}
              disabled={phase === "submitting"}
            />
            <textarea
              class="mf-textarea mf-textarea-compact"
              placeholder="More details (optional)"
              value={description}
              onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              maxLength={4000}
              disabled={phase === "submitting"}
              rows={2}
            />
            <input
              class="mf-input"
              type="email"
              placeholder="Your email (optional)"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              disabled={phase === "submitting"}
            />
          </div>
          <div class="mf-footer-meta-row">
            <span class="mf-overlay-meta">{annotationCountLabel(annotations)}</span>
            <div class="mf-actions">
              <button
                type="button"
                class="mf-btn mf-btn-ghost"
                onClick={onClose}
                disabled={phase === "submitting"}
              >
                Cancel
              </button>
              <button
                type="button"
                class="mf-btn mf-btn-primary"
                onClick={submit}
                disabled={phase === "submitting" || !title.trim()}
              >
                {phase === "submitting" ? (
                  "Sending…"
                ) : (
                  <>
                    <SendIcon /> Send feedback
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function toolHint(tool: Tool): preact.JSX.Element {
  if (tool === "pin")
    return (
      <>
        <CommentPinIcon /> Optional — click a spot to point at the problem
      </>
    );
  if (tool === "rectangle")
    return (
      <>
        <RectangleIcon /> Drag to highlight an area
      </>
    );
  if (tool === "arrow")
    return (
      <>
        <ArrowToolIcon /> Drag to point at something
      </>
    );
  return (
    <>
      <PencilIcon /> Draw to circle or scribble on the screenshot
    </>
  );
}

function annotationCountLabel(annotations: Annotation[]): string {
  if (annotations.length === 0) return "Marking the screenshot is optional — just describe the issue and send";
  const pins = annotations.filter((a) => a.type === "pin").length;
  const shapes = annotations.length - pins;
  const parts: string[] = [];
  if (pins) parts.push(`${pins} pin${pins === 1 ? "" : "s"}`);
  if (shapes) parts.push(`${shapes} mark${shapes === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

function ToolButton({
  current,
  value,
  onSelect,
  label,
  children,
}: {
  current: Tool;
  value: Tool;
  onSelect: (t: Tool) => void;
  label: string;
  children: preact.ComponentChildren;
}) {
  return (
    <button
      type="button"
      class={`mf-tool ${current === value ? "mf-tool-active" : ""}`}
      onClick={() => onSelect(value)}
      title={label}
      aria-label={label}
      aria-pressed={current === value}
    >
      {children}
    </button>
  );
}

function ThicknessButton({
  active,
  dotPx,
  label,
  onSelect,
}: {
  active: boolean;
  dotPx: number;
  label: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      class={`mf-tool mf-tool-thickness ${active ? "mf-tool-active" : ""}`}
      onClick={onSelect}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      <span
        class="mf-thickness-dot"
        style={{ width: dotPx, height: dotPx }}
        aria-hidden="true"
      />
    </button>
  );
}

type ImgNat = { w: number; h: number };

// Stroke width in NATIVE pixels — identical to rasterizeAnnotations. Because
// the SVG viewBox is the native image size and scales uniformly to the display,
// this renders on screen exactly as thick as it will appear in the saved image.
function nativeStrokeFor(nat: ImgNat, pct: number): number {
  return Math.max(2, (Math.min(nat.w, nat.h) * pct) / 100);
}

function ShapeSvg({ annotation, imgNat }: { annotation: Annotation; imgNat: ImgNat | null }) {
  if (annotation.type === "pin") return null;

  // The SVG viewBox is "0 0 natW natH", so all coordinates below are in native
  // pixels — the same space rasterizeAnnotations draws in. No non-scaling-stroke
  // trickery: the whole viewBox scales uniformly to the displayed image.
  const nat = imgNat ?? { w: 100, h: 100 };
  const px = (pct: number, span: number) => (pct / 100) * span;
  const stroke = ANNOTATION_COLOR;
  const strokeWidth = nativeStrokeFor(nat, strokeWidthPctOf(annotation));

  if (annotation.type === "rectangle") {
    const x = px(Math.min(annotation.x1Pct, annotation.x2Pct), nat.w);
    const y = px(Math.min(annotation.y1Pct, annotation.y2Pct), nat.h);
    const w = px(Math.abs(annotation.x2Pct - annotation.x1Pct), nat.w);
    const h = px(Math.abs(annotation.y2Pct - annotation.y1Pct), nat.h);
    // Stroke width MUST go through inline style: Preact passes the camelCase
    // `strokeWidth` attribute through verbatim and the browser ignores it (it
    // wants `stroke-width`), so the line would fall back to the 1px default.
    return (
      <rect x={x} y={y} width={w} height={h} fill="none" stroke={stroke} style={{ strokeWidth }} />
    );
  }

  if (annotation.type === "arrow") {
    return <ArrowSvg shape={annotation} stroke={stroke} nat={nat} />;
  }

  if (annotation.type === "freedraw") {
    const d = freeDrawSvgPath(
      annotation.pointsPct.map((p) => ({ x: px(p.xPct, nat.w), y: px(p.yPct, nat.h) })),
    );
    if (!d) return null;
    return (
      <path
        d={d}
        fill="none"
        stroke={stroke}
        style={{ strokeWidth, strokeLinecap: "round", strokeLinejoin: "round" }}
      />
    );
  }

  return null;
}

function ArrowSvg({ shape, stroke, nat }: { shape: Arrow; stroke: string; nat: ImgNat }) {
  // Everything in native pixels — identical geometry to rasterizeAnnotations so
  // the live arrow matches the burned one (shaft thickness, head angle, length).
  const x1 = (shape.x1Pct / 100) * nat.w;
  const y1 = (shape.y1Pct / 100) * nat.h;
  const x2 = (shape.x2Pct / 100) * nat.w;
  const y2 = (shape.y2Pct / 100) * nat.h;
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len < 1) {
    // Pre-drag preview — no arrow yet.
    return null;
  }
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const strokeWidth = nativeStrokeFor(nat, strokeWidthPctOf(shape));
  const headLen = Math.max(12, strokeWidth * 4);
  const headAngle = Math.PI / 7;
  const hx1 = x2 - headLen * Math.cos(angle - headAngle);
  const hy1 = y2 - headLen * Math.sin(angle - headAngle);
  const hx2 = x2 - headLen * Math.cos(angle + headAngle);
  const hy2 = y2 - headLen * Math.sin(angle + headAngle);
  return (
    <g
      fill="none"
      stroke={stroke}
      style={{ strokeWidth, strokeLinecap: "round", strokeLinejoin: "round" }}
    >
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      <line x1={x2} y1={y2} x2={hx1} y2={hy1} />
      <line x1={x2} y1={y2} x2={hx2} y2={hy2} />
    </g>
  );
}

function PinMarker({
  pin,
  index,
  active,
  onActivate,
  disabled,
}: {
  pin: Pin;
  index: number;
  active: boolean;
  onActivate: () => void;
  disabled: boolean;
}) {
  // Marker only — the note editor is rendered once at stage level (see
  // ScreenshotMode) so it isn't trapped by this wrapper's stacking context.
  return (
    <div
      class="mf-pin-wrap"
      style={{ left: `${pin.xPct}%`, top: `${pin.yPct}%` }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        class={`mf-pin-marker ${active ? "is-active" : ""}`}
        onClick={onActivate}
        aria-label={`Pin ${index}`}
        disabled={disabled}
      >
        {index}
      </button>
    </div>
  );
}

